/**
 * Tiptap (ProseMirror) ベースのリッチテキストエディタ directive (実験実装)
 *
 * Tiptap は headless なのでツールバー DOM は自前で組む。部品と純ロジックは richTextEditor* へ分けて
 * あり、ここは Editor 生成・ngModel 連携・配線を担う。angular を import するのでこのファイルだけは
 * jest でロードできない
 *
 * @property {string} ng-model - 編集対象 HTML (双方向)
 * @property {boolean} [editable=true] - 編集可否
 * @property {Editor} [editor] - Tiptap Editor インスタンスを親 scope に公開
 * @property {Function} [on-change] - 変更時 ({contents})
 * @property {Function} [on-image-upload] - 画像アップロード前 ({files}) ※本実験では画像ボタン未提供のため発火しない
 * @property {Function} [on-paste] - ペースト時 ({evt})
 * @property {object} [config] - config.buttons でツールバー構成を指定 (未対応 token は無視)
 */
import { Editor, getMarkRange } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { TextStyle, Color, BackgroundColor, FontFamily, FontSize } from '@tiptap/extension-text-style';
import { TableKit } from '@tiptap/extension-table';
import Image from '@tiptap/extension-image';
import { sanitizeRichTextHtml } from '@/util/html/sanitizeRichTextHtml';
import { BlockLayout, YoutubeIframe } from './richTextEditor/extensions';
import {
  convertYoutubeAnchorsInHtml,
  logPasteError,
  transformPasted,
  transformPastedHTML,
} from './richTextEditor/paste';
import { DEFAULT_BUTTONS, FONTSIZE_LIST, FONT_LIST, STYLE_LIST } from './richTextEditor/config';
import { createToolbarBuilder } from './richTextEditor/toolbar';
import { createPopupController } from './richTextEditor/popups';
import { createPopupOpeners } from './richTextEditor/popupActions';
import {
  markJoinedItems,
  normalizeFontFamily,
  normalizeFontSize,
  paragraphValueOf,
  parseToolbarSpec,
} from './richTextEditor/state';

angular.module('inspinia').directive('codmonRichTextEditor', [
  '$timeout',
  ($timeout) => ({
    restrict: 'E',
    require: '?ngModel',
    scope: {
      config: '=?',
      editor: '=?',
      editable: '=?',
      onChange: '&?',
      onImageUpload: '&?',
      onPaste: '&?',
    },
    template:
      '<div class="codmon-rich-text-editor">' +
      '<div class="codmon-rte__toolbar" role="toolbar"></div>' +
      '<div class="codmon-rte__content"></div>' +
      '</div>',
    link($scope, $element, $attrs, ngModel) {
      const root = $element[0].querySelector('.codmon-rich-text-editor');
      const toolbarEl = root.querySelector('.codmon-rte__toolbar');
      const contentEl = root.querySelector('.codmon-rte__content');
      const userConfig = $scope.config || {};

      // 開いているポップアップと外側クリック用の document リスナは controller が持つ。解放は destroy() だけ
      const popups = createPopupController({ toolbarEl });

      const editable = $scope.editable === undefined ? true : !!$scope.editable;

      const editorInstance = new Editor({
        element: contentEl,
        editable: editable,
        extensions: [
          StarterKit.configure({
            link: {
              openOnClick: false,
              autolink: true, // 入力中の URL を自動でリンク化する
              // リンクは常に同じウィンドウで開く (別ウィンドウのオプションは設けない)
              HTMLAttributes: { target: '_self' },
            },
          }),
          TextStyle,
          Color,
          BackgroundColor,
          FontFamily,
          FontSize,
          TableKit.configure({
            table: { resizable: true, HTMLAttributes: { class: 'table table-bordered' } },
          }),
          BlockLayout,
          YoutubeIframe,
          // ローカル画像のコピペ / D&D は data: URI になるので、既定の allowBase64:false で弾かれる
          Image.configure({ allowBase64: false }),
        ],
        editorProps: {
          attributes: { class: 'codmon-rte__prosemirror' },
          // 掃除は文字列段、YouTube 埋め込みは slice 段、通知は副作用として分ける。挿入は既定に任せる
          transformPastedHTML: (html) => {
            try {
              return transformPastedHTML(html);
            } catch (error) {
              logPasteError(error);
              return html;
            }
          },
          transformPasted: (slice, view) => {
            try {
              return transformPasted(slice, view);
            } catch (error) {
              logPasteError(error);
              return slice;
            }
          },
          handleDOMEvents: {
            paste: (view, event) => {
              if ($attrs.onPaste) $scope.$applyAsync(() => $scope.onPaste({ evt: event }));
              return false;
            },
            // OS からのファイルドロップは ProseMirror が preventDefault しないため、Safari では file:// へ遷移してしまう。
            // ローカル画像は受け付けない方針 (allowBase64:false と同じ) なので、ここで止めるだけでよい
            drop: (view, event) => {
              if (event.dataTransfer && event.dataTransfer.files.length > 0) {
                event.preventDefault();
                return true;
              }
              return false;
            },
          },
        },
        onUpdate: ({ editor }) => {
          $timeout(() => {
            if (editor.isDestroyed) return;
            const contents = editor.isEmpty ? '' : sanitizeRichTextHtml(editor.getHTML());
            if (ngModel && ngModel.$viewValue !== contents) ngModel.$setViewValue(contents);
            if ($attrs.onChange) $scope.onChange({ contents });
          }, 0);
        },
        onSelectionUpdate: () => syncToolbarState(),
        onTransaction: () => syncToolbarState(),
        onBlur: () => convertYoutubeAnchors(),
      });
      $scope.editor = editorInstance;

      // YouTube URL の <a> を埋め込みに変換する。入力ごとに走らせると重いので blur でまとめる
      const convertYoutubeAnchors = () => {
        const { changed, html } = convertYoutubeAnchorsInHtml(editorInstance.getHTML());
        if (changed) {
          editorInstance.commands.setContent(sanitizeRichTextHtml(html));
        }
      };

      // 保存済みテンプレートを開いたときに、API から入った本文をエディタへ流し込む処理。
      // Angular はモデルの変更を自動で反映しないので、ここで setContent する。
      if (ngModel) {
        ngModel.$render = () => {
          const modelHtml = ngModel.$viewValue == null ? '' : ngModel.$viewValue;
          const sanitizedModelHtml = sanitizeRichTextHtml(modelHtml);
          // モデルの状態とエディタの状態が両方とも空の場合には何もしない
          // tiptapは空の状態を<p></p>で保持するので純粋に空の状態を比較するにはeditorInstance.isEmptyを見る必要がある
          const editorContents = editorInstance.isEmpty ? '' : sanitizeRichTextHtml(editorInstance.getHTML());
          if (editorContents === sanitizedModelHtml) {
            return;
          }
          // 同じ内容なら呼ばない。setContent はカーソル位置と undo 履歴を捨てる
          editorInstance.commands.setContent(sanitizedModelHtml, { emitUpdate: false });
          // setContent 後はカーソルが末尾に来る。末尾の装飾がツールバーに出るので先頭へ戻す
          editorInstance.commands.setTextSelection(0);
          syncToolbarState();
        };
      }

      const toolbar = createToolbarBuilder({ toolbarEl, editor: editorInstance, popups });
      const syncToolbarState = () => toolbar.syncState();
      const syncToolbarStateAfterAction = () => toolbar.syncStateAfterAction();

      const currentParagraphValue = () => paragraphValueOf(editorInstance);
      const applyParagraphValue = (value) => {
        const chain = editorInstance.chain().focus();
        if (value === 'p') chain.setParagraph().run();
        else if (value === 'blockquote') chain.setParagraph().setBlockquote().run();
        else if (value === 'pre') chain.setCodeBlock().run();
        else chain.setHeading({ level: Number(value.slice(1)) }).run();
      };

      // ポップアップから呼ぶエディタ操作。どれをいつ使うかは popupActions が決め、ここは実装だけ持つ
      const chain = () => editorInstance.chain().focus();
      const editorActions = {
        blockAttr: (name) =>
          editorInstance.getAttributes('paragraph')[name] || editorInstance.getAttributes('heading')[name] || null,
        setBackgroundColor: (color) => chain().setBackgroundColor(color).run(),
        unsetBackgroundColor: () => chain().unsetBackgroundColor().run(),
        setColor: (color) => chain().setColor(color).run(),
        unsetColor: () => chain().unsetColor().run(),
        setTextAlign: (align) => chain().setBlockTextAlign(align).run(),
        changeIndent: (delta) => chain().changeBlockIndent(delta).run(),
        setLineHeight: (lineHeight) => chain().setBlockLineHeight(lineHeight).run(),
        insertTable: (rows, cols) => chain().insertTable({ rows, cols, withHeaderRow: false }).run(),
        readLinkContext: () => {
          const attrs = editorInstance.getAttributes('link');
          const { selection } = editorInstance.state;
          // 未選択で既存リンク上にいるときはリンク全体へ広げる。無いと textBetween が空になり表示テキストが出ない
          const range =
            selection.empty && attrs.href
              ? getMarkRange(editorInstance.state.doc.resolve(selection.from), editorInstance.schema.marks.link)
              : null;
          return {
            href: attrs.href || '',
            text: editorInstance.state.doc.textBetween(
              range ? range.from : selection.from,
              range ? range.to : selection.to
            ),
            range: range || null,
            isSelectionEmpty: selection.empty,
          };
        },
        // setLink はマークを足すだけなので、他のマークを壊さない
        updateLinkAttrs: (attrs) => chain().extendMarkRange('link').setLink(attrs).run(),
        // 文字列連結は引用符で構造が壊れるので JSONContent で渡す
        replaceLink: (range, attrs, text) =>
          chain()
            .insertContentAt(range, { type: 'text', text, marks: [{ type: 'link', attrs }] })
            .run(),
        insertLink: (attrs, text) =>
          chain()
            .insertContent({ type: 'text', text, marks: [{ type: 'link', attrs }] })
            .run(),
        setLinkOnSelection: (attrs) => chain().setLink(attrs).run(),
        unsetLink: () => chain().unsetLink().run(),
      };

      const openers = createPopupOpeners({
        actions: editorActions,
        popups,
        afterAction: syncToolbarStateAfterAction,
      });

      const builders = {
        bold: () =>
          toolbar.commandButton({
            iconName: 'bold',
            label: '太字',
            isActive: () => editorInstance.isActive('bold'),
            onClick: () => editorInstance.chain().focus().toggleBold().run(),
          }),
        italic: () =>
          toolbar.commandButton({
            iconName: 'italic',
            label: '斜体',
            isActive: () => editorInstance.isActive('italic'),
            onClick: () => editorInstance.chain().focus().toggleItalic().run(),
          }),
        underline: () =>
          toolbar.commandButton({
            iconName: 'underline',
            label: '下線',
            isActive: () => editorInstance.isActive('underline'),
            onClick: () => editorInstance.chain().focus().toggleUnderline().run(),
          }),
        eraser: () =>
          toolbar.commandButton({
            iconName: 'eraser',
            label: 'スタイルを解除',
            onClick: () => editorInstance.chain().focus().unsetAllMarks().run(),
          }),
        ul: () =>
          toolbar.commandButton({
            iconName: 'ul',
            label: '箇条書きリスト',
            isActive: () => editorInstance.isActive('bulletList'),
            onClick: () => editorInstance.chain().focus().toggleBulletList().run(),
          }),
        ol: () =>
          toolbar.commandButton({
            iconName: 'ol',
            label: '段落番号付きリスト',
            isActive: () => editorInstance.isActive('orderedList'),
            onClick: () => editorInstance.chain().focus().toggleOrderedList().run(),
          }),
        table: () =>
          toolbar.triggerButton({
            iconName: 'table',
            label: 'テーブル',
            popupClass: 'codmon-rte__table-popup',
            openPopup: openers.openTable,
          }),
        brush: () =>
          toolbar.triggerButton({
            iconName: 'brush',
            label: '使用中の色',
            popupClass: 'codmon-rte__color-popup',
            openPopup: openers.openColor,
            // brush アイコンに現在の色を映す。未フォーカス時は中立表示にするため色を取らない
            onUpdate: (button, isFocused) => {
              const style = isFocused ? editorInstance.getAttributes('textStyle') : {};
              button.setIconColors({ color: style.color, backgroundColor: style.backgroundColor });
            },
          }),
        align: () =>
          toolbar.triggerButton({
            iconName: 'align',
            label: '段落',
            popupClass: 'codmon-rte__align-popup',
            openPopup: openers.openAlign,
          }),
        lineHeight: () =>
          toolbar.triggerButton({
            iconName: 'lineHeight',
            label: '行の高さ',
            popupClass: 'codmon-rte__lineheight-popup',
            openPopup: openers.openLineHeight,
          }),
        link: () =>
          toolbar.triggerButton({
            iconName: 'link',
            label: 'リンク',
            popupClass: 'codmon-rte__link-popup',
            openPopup: openers.openLink,
            isActive: () => editorInstance.isActive('link'),
          }),
        font: () =>
          toolbar.selectBox({
            ref: 'font',
            label: 'フォント',
            placeholder: 'フォント',
            items: FONT_LIST,
            getValue: () => normalizeFontFamily(editorInstance.getAttributes('textStyle').fontFamily),
            onSelect: (value) => editorInstance.chain().focus().setFontFamily(value).run(),
            renderItem: (item) => `<span class="codmon-rte__font-item">${item.label}</span>`,
          }),
        fontsize: () =>
          toolbar.selectBox({
            ref: 'fontsize',
            label: '文字サイズ',
            placeholder: '13',
            items: FONTSIZE_LIST.map((v) => ({ value: v, label: v })),
            getValue: () => normalizeFontSize(editorInstance.getAttributes('textStyle').fontSize),
            onSelect: (value) =>
              editorInstance
                .chain()
                .focus()
                .setFontSize(value + 'px')
                .run(),
            renderItem: (item) => `<span class="codmon-rte__fontsize-item">${item.label}</span>`,
          }),
        paragraph: () =>
          toolbar.selectBox({
            ref: 'paragraph',
            label: 'スタイル',
            placeholder: '本文',
            items: STYLE_LIST,
            getValue: currentParagraphValue,
            onSelect: applyParagraphValue,
            renderItem: (item) =>
              `<span class="codmon-rte__style-item codmon-rte__style-item--${item.value}">${item.label}</span>`,
          }),
      };

      const buildToolbar = () => {
        for (const token of parseToolbarSpec(userConfig.buttons || DEFAULT_BUTTONS)) {
          if (token === '|') toolbar.divider();
          else if (builders[token]) builders[token]();
        }
        markJoinedItems(toolbarEl);
      };

      buildToolbar();
      if (ngModel && ngModel.$render) ngModel.$render();
      syncToolbarState();

      $element.on('$destroy', () => {
        popups.destroy();
        editorInstance.destroy();
      });
    },
  }),
]);
